import { ts } from '@shared/lib/diagTs';
import * as signalR from '@microsoft/signalr';
import { devAuthUser, env } from '@config/env';
import { ApiClient, DEV_USER_HEADER } from '@core/api/client';
import { lastRestInstanceId, seenRestInstanceIds } from '@core/api/serverInstance';
import { alignHubWithRest, type HubAffinityOutcome } from '@core/realtime/hubAffinity';
import {
  type AutoResumeLatchReason, latchAutoResume, readAutoResumeLatch, releaseAutoResume,
} from '@core/realtime/autoResumeLatch';
import { getRelayOwnership, type RelayOwnership } from '@core/realtime/relayOwnership';
import { consumeBootMarker, describeBoot, registerReloadLogSink } from '@core/reloadReason';
import { decodeAndApplyServerMsg, resetContractResolution } from '@features/quotes/cqgQuoteDecoder';
import { decodeAndApplyPositions, resetPositionLots } from '@features/quotes/cqgPositionDecoder';
import { cqg } from '@features/quotes/proto/cqgQuotes';
import { livePositionsStore } from '@features/positions/lib/livePositionsStore';
import { isPureMarketData } from './cqgUiState';
import { readStoredCqgCreds } from './cqgCreds';

// ─── FE-built Logon (2026-07-08: CQG creds are FORBIDDEN from transiting our BE) ────────────────
// The browser holds the credentials (localStorage) and sends the CQG Logon ITSELF as the first
// frame on every fresh socket. The BE only pushes CqgOpenSocket with the non-secret logon
// parameters below (single source of truth: BE CqgOptions → CqgLogonParameters).

/** Non-secret logon parameters pushed by the BE alongside every CqgOpenSocket. */
export interface CqgLogonParams {
  privateLabel: string;
  clientAppId: string;
  clientVersion: string;
  protocolVersionMajor: number;
  protocolVersionMinor: number;
}

/** user_session_2 SessionSetting: SESSION_SETTING_ALLOW_SESSION_RESTORE. */
const SESSION_SETTING_ALLOW_RESTORE = 1;

/**
 * Accept the CqgOpenSocket params payload in either JSON casing (SignalR's default camelCase or a
 * PascalCase fallback) and reject anything incomplete. Null ⇒ the push carried no usable params.
 */
export function normalizeLogonParams(raw: unknown): CqgLogonParams | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const s = (a: unknown, b: unknown) => (typeof a === 'string' && a) || (typeof b === 'string' && b) || null;
  const n = (a: unknown, b: unknown) => (typeof a === 'number' && a) || (typeof b === 'number' && b) || null;
  const privateLabel = s(o.privateLabel, o.PrivateLabel);
  const clientAppId = s(o.clientAppId, o.ClientAppId);
  const clientVersion = s(o.clientVersion, o.ClientVersion);
  const protocolVersionMajor = n(o.protocolVersionMajor, o.ProtocolVersionMajor);
  const protocolVersionMinor = n(o.protocolVersionMinor, o.ProtocolVersionMinor);
  if (!privateLabel || !clientAppId || !clientVersion || !protocolVersionMajor || !protocolVersionMinor) return null;
  return { privateLabel, clientAppId, clientVersion, protocolVersionMajor, protocolVersionMinor };
}

// One-shot take-over flag: ONLY an explicit user "take over" click arms it; the NEXT Logon carries
// drop_concurrent_session=true and the flag resets. Silent resumes/reconnects never set it, so they
// DETECT a concurrent session (CQG code 105 → the take-over prompt) instead of kicking the user's
// trading platform (the old logon-war behaviour). Mirrors the BE's deleted _pendingDropConcurrent.
let _nextLogonDropConcurrent = false;

/** Arm drop_concurrent_session for the NEXT FE-built Logon only (explicit take-over clicks). */
export function setNextLogonDropConcurrent(): void {
  _nextLogonDropConcurrent = true;
}

/** Read-and-clear the one-shot take-over flag (consumed by the next Logon build). */
export function consumeNextLogonDropConcurrent(): boolean {
  const armed = _nextLogonDropConcurrent;
  _nextLogonDropConcurrent = false;
  return armed;
}

/** Build the CQG Logon frame from stored creds + BE-pushed params. Exported for tests. */
export function buildLogonFrame(
  creds: { username: string; password: string },
  params: CqgLogonParams,
  dropConcurrentSession: boolean,
): Uint8Array {
  return cqg.ClientMsg.encode({
    logon: {
      userName: creds.username,
      password: creds.password,
      privateLabel: params.privateLabel,
      clientAppId: params.clientAppId,
      clientVersion: params.clientVersion,
      protocolVersionMajor: params.protocolVersionMajor,
      protocolVersionMinor: params.protocolVersionMinor,
      sessionSettings: [SESSION_SETTING_ALLOW_RESTORE],
      ...(dropConcurrentSession ? { dropConcurrentSession: true } : {}),
    },
  }).finish();
}

// ─── Position-snapshot gate ──────────────────────────────────────────────────
// On connect CQG sends a SNAPSHOT of current positions (the PositionStatuses frames) BEFORE the
// TradeSnapshotCompletions frame. useOpenPositions gates on this: BEFORE the snapshot is ready it
// shows the BE /positions rows verbatim (don't blank the user mid-connect); AFTER it, the CQG live set
// is authoritative (a BE row not in the live set is treated as CLOSED). Reset on each fresh socket open
// (resetPositionSnapshot); flipped true on the first TradeSnapshotCompletions (markPositionSnapshotReady).
let positionSnapshotReady = false;

/** Whether the initial CQG position snapshot has completed this session (the merge then trusts CQG). */
export function isPositionSnapshotReady(): boolean {
  return positionSnapshotReady;
}

const snapshotReadyListeners = new Set<() => void>();

/** Subscribe to position-snapshot-ready changes (for useSyncExternalStore). Returns an unsubscribe. */
export function onPositionSnapshotReadyChange(listener: () => void): () => void {
  snapshotReadyListeners.add(listener);
  return () => { snapshotReadyListeners.delete(listener); };
}

function emitSnapshotReady(): void {
  snapshotReadyListeners.forEach(l => { try { l(); } catch { /* ignore */ } });
}

/** Relay calls this on each fresh CQG socket open (new session) — re-arm the snapshot gate. */
function resetPositionSnapshot(): void {
  if (!positionSnapshotReady) return;
  positionSnapshotReady = false;
  emitSnapshotReady();
}

/** Relay calls this the first time it sees a TradeSnapshotCompletions frame this session. */
function markPositionSnapshotReady(): void {
  if (positionSnapshotReady) return;
  positionSnapshotReady = true;
  if (import.meta.env.DEV)
    console.log(ts(), '[live-position] ✅ CQG position snapshot complete - CQG is now authoritative for open positions');
  emitSnapshotReady();
}

/**
 * Browser-side CQG WebSocket relay.
 *
 * Mirrors the legacy `C:\GIT\Optimus\Journal\source\Components\wwwroot\components\cqg-connector.js`
 * (which was Blazor's JSInterop bridge from the .NET handlers to a real browser WS): the BE owns
 * all CQG Protobuf logic; the browser is just an opaque byte pipe to `wss://demoapi.cqg.com/`
 * so CQG sees the user's IP / Origin rather than the API pod's.
 *
 * Every state transition + every byte count is also forwarded to the BE via `CqgClientLog` so
 * the backend log shows both sides of the conversation — handy when the browser WS open / close
 * is the thing that fails.
 */
/**
 * BroadcastChannel name for cross-tab CQG ownership detection. Mirrors the legacy app's
 * single-tab guard: when a second tab opens a relay, the older tab logs a warning so the user
 * gets a hint at why quotes might flicker. Full enforcement (auto-release in the older tab) is
 * deferred to v2 — v1 is log-only so we don't accidentally close a session the user wants to
 * keep.
 */

/** Browser→CQG WebSocket liveness for this tab. 'open' = a real socket is connected right now. */
/**
 * This tab's browser→CQG socket. `open` = the WebSocket is up and the Logon has been WRITTEN;
 * `live` = CQG acked that Logon on THIS socket (LogonResult result_code 0). FE-built frames
 * (`sendClient`) wait for `live`: CQG answers anything sent between the Logon and its ack with
 * `101 You are not logged in` (prod 2026-09-12: every fresh session's first symbol resolutions).
 */
export type RelayConnState = 'closed' | 'opening' | 'open' | 'live';

/** The socket is usable for data (CQG accepted the WebSocket); `live` additionally means the logon is acked. */
export function isSocketUp(state: RelayConnState): boolean {
  return state === 'open' || state === 'live';
}

/**
 * The inbound frame's LogonResult result_code, or null when the frame carries none. Exported so the
 * live-gate is testable without a socket.
 */
export function parseLogonResultCode(summary: string, bytes: Uint8Array): number | null {
  if (!summary.includes('LogonResult')) return null;
  const r = decodeSubmessageFields(bytes, 100, LOGON_RESULT_FIELDS);
  return typeof r.result_code === 'number' ? r.result_code : null;
}

// ServerMsg field numbers from webapi_2.proto / Webapi2.cs. Used to make inbound frames
// self-describing in the console (e.g. "LogonResult, OrderStatuses×3 bytes=210") instead of
// the opaque "bytes=210". Keep this in sync with webapi_2.proto ServerMsg — an unmapped field
// prints as `fieldN` (the old "field24" mystery = AccountSummaryStatuses, the balance/margin push).
const SERVER_MSG_FIELDS: Record<number, string> = {
  1:   'InformationReports',
  2:   'OrderRequestRejects',
  3:   'TradeSubscriptionStatuses',
  4:   'TradeSnapshotCompletions',
  5:   'OrderStatuses',
  6:   'PositionStatuses',
  7:   'CollateralStatuses',
  8:   'MarketDataSubscriptionStatuses',
  9:   'RealTimeMarketData',
  15:  'RuleResults',
  16:  'AccountLogonResults',
  24:  'AccountSummaryStatuses',
  25:  'OrderRequestAcks',
  28:  'ExchangePositionStatuses',
  29:  'ExchangeBalanceStatuses',
  30:  'GoFlatStatuses',
  32:  'AllocationReports',
  33:  'CqgReports',
  100: 'LogonResult',
  101: 'RestoreOrJoinSessionResult',
  102: 'LoggedOff',
  103: 'PasswordChangeResult',
  104: 'UserMessages',
  105: 'ObtainDemoCredentialsResults',
  106: 'UserSessionStateResults',
  107: 'Ping',
  108: 'Pong',
  109: 'LogonRoutineServer',
  110: 'PasswordChangeRoutineServer',
  111: 'ConcurrentConnectionJoinResults',
  112: 'SetCollapsingLevelResults',
};

/**
 * Walk a ServerMsg payload at the wire level (no proto runtime) and produce a short summary
 * like "LogonResult" or "RealTimeMarketData×3, OrderStatuses". Stops on any malformed tag and
 * returns "?" — this is debug-only, never thrown.
 */
function describeServerMsg(bytes: Uint8Array): string {
  try {
    const counts = new Map<string, number>();
    let i = 0;
    while (i < bytes.length) {
      // Decode tag varint
      let tag = 0, shift = 0, byte = 0;
      do {
        if (i >= bytes.length) return summarize(counts) || '?';
        byte = bytes[i++]!;
        tag |= (byte & 0x7f) << shift;
        shift += 7;
        if (shift > 35) return summarize(counts) || '?';
      } while (byte & 0x80);
      const fieldNumber = tag >>> 3;
      const wireType = tag & 0x7;
      const name = SERVER_MSG_FIELDS[fieldNumber] ?? `field${fieldNumber}`;
      counts.set(name, (counts.get(name) ?? 0) + 1);
      // Skip value based on wire type
      if (wireType === 0) {
        // varint
        while (i < bytes.length && (bytes[i++]! & 0x80)) { /* keep reading */ }
      } else if (wireType === 1) {
        i += 8;
      } else if (wireType === 2) {
        let len = 0, ls = 0, lb = 0;
        do {
          if (i >= bytes.length) return summarize(counts) || '?';
          lb = bytes[i++]!;
          len |= (lb & 0x7f) << ls;
          ls += 7;
        } while (lb & 0x80);
        i += len;
      } else if (wireType === 5) {
        i += 4;
      } else {
        return summarize(counts) || '?';
      }
    }
    return summarize(counts) || '?';
  } catch {
    return '?';
  }
}

function summarize(counts: Map<string, number>): string {
  const parts: string[] = [];
  for (const [name, n] of counts) parts.push(n > 1 ? `${name}×${n}` : name);
  return parts.join(',');
}

/**
 * CREDENTIAL REDACTION for the session-tape mirror: true when the frame has ANY top-level field
 * number >= 100 — CQG's session-management range (ClientMsg.logon=100 carries the PLAINTEXT
 * password; restore/password-change carry tokens/secrets). Such frames must never be mirrored to
 * the BE via CqgFeFrame. Malformed frames return true (fail CLOSED — never mirror what we can't
 * classify). Mirrors the BE `CqgS3FrameRecorder.ContainsSessionLevelField` exactly.
 */
export function hasSessionLevelField(bytes: Uint8Array): boolean {
  try {
    let i = 0;
    while (i < bytes.length) {
      let tag = 0, shift = 0, byte = 0;
      do {
        if (i >= bytes.length || shift > 35) return true;
        byte = bytes[i++]!;
        tag |= (byte & 0x7f) << shift;
        shift += 7;
      } while (byte & 0x80);
      const fieldNumber = tag >>> 3;
      const wireType = tag & 0x7;
      if (fieldNumber >= 100) return true;
      if (wireType === 0) {
        while (i < bytes.length && (bytes[i++]! & 0x80)) { /* varint */ }
      } else if (wireType === 1) {
        i += 8;
      } else if (wireType === 2) {
        let len = 0, ls = 0, lb = 0;
        do {
          if (i >= bytes.length || ls > 35) return true;
          lb = bytes[i++]!;
          len |= (lb & 0x7f) << ls;
          ls += 7;
        } while (lb & 0x80);
        if (len < 0 || i + len > bytes.length) return true;
        i += len;
      } else if (wireType === 5) {
        i += 4;
      } else {
        return true; // unknown wire type — fail closed
      }
    }
    return false;
  } catch {
    return true; // fail closed
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

// ─── CQG logon diagnostics (demo/dev/local only — NEVER prod) ────────────────────────────────────
// A code-101 "Username or password is incorrect" rejection is CQG's GENERIC auth failure — it fires
// for both wrong creds AND an app-id/endpoint mismatch. To tell them apart we decode, in the browser
// console, exactly what we SENT (the outbound Logon: client_app_id / user_name — password REDACTED)
// and what CQG RETURNED (the inbound LogonResult: result_code + text_message + raw base64). Gated on
// MODE !== 'prod' (prod builds with `--mode prod`; dev with `--mode dev`, demo not prod, local
// 'development') — NOT import.meta.env.DEV, which is false on the deployed demo build we test against.
const CQG_DIAG = import.meta.env.MODE !== 'prod';

const RESULT_CODE_LABEL: Record<number, string> = {
  0: 'SUCCESS', 101: 'FAILURE', 103: 'NO_ONETIME_PASSWORD', 104: 'PASSWORD_EXPIRED',
  105: 'CONCURRENT_SESSION', 106: 'REDIRECTED', 107: 'ROUTINE_ERROR', 108: 'ACCESS_TOKEN_EXPIRED',
};

// Field number → [name, kind]. kind: 'varint' | 'str' | 'redact' (present-but-never-logged).
type DiagField = readonly [name: string, kind: 'varint' | 'str' | 'redact'];
// ClientMsg.logon (field 100) → Logon (user_session_2.proto). Exported for the diag decoder test.
export const LOGON_REQUEST_FIELDS: Record<number, DiagField> = {
  1: ['user_name', 'str'], 2: ['password', 'redact'], 3: ['one_time_password', 'redact'],
  4: ['private_label', 'str'], 5: ['client_app_id', 'str'], 6: ['client_version', 'str'],
  7: ['drop_concurrent_session', 'varint'], 14: ['access_token', 'redact'], 19: ['partner_token', 'redact'],
};
// ServerMsg.logon_result (field 100) → LogonResult.
export const LOGON_RESULT_FIELDS: Record<number, DiagField> = {
  1: ['result_code', 'varint'], 2: ['base_time', 'str'], 3: ['session_token', 'redact'],
  4: ['text_message', 'str'], 5: ['redirect_url', 'str'],
};
// ServerMsg.logged_off (field 102) → LoggedOff. This is CQG's authoritative "why you're being logged
// off" — decode it so a disconnect is never a guess.
export const LOGGED_OFF_FIELDS: Record<number, DiagField> = {
  1: ['logoff_reason', 'varint'], 2: ['text_message', 'str'], 3: ['redirect_url', 'str'],
};
const LOGOFF_REASON_LABEL: Record<number, string> = {
  1: 'BY_REQUEST', 2: 'REDIRECTED', 3: 'FORCED (server-side reason)',
  4: 'REASSIGNED (session taken over by another connection = concurrent/restore)',
};
// ServerMsg.user_messages (field 104, repeated) → UserMessage. CQG surfaces protocol violations,
// forced-logoff details, rate-limit notices, etc. here — the other half of "why it closed".
export const USER_MESSAGE_FIELDS: Record<number, DiagField> = {
  1: ['message_type', 'varint'], 2: ['source', 'str'], 3: ['subject', 'str'], 4: ['text', 'str'],
};
const USER_MESSAGE_TYPE_LABEL: Record<number, string> = {
  1: 'CRITICAL_ERROR', 2: 'WARNING', 3: 'INFO', 4: 'LOG',
};
// ServerMsg.concurrent_connection_join_results (field 111, repeated) → ConcurrentConnectionJoin. This
// is CQG's EXPLICIT "another connection joined your session" notice: is_same_app_type=true means the
// new session is the SAME app (same private_label + client_app_id) — e.g. the deployed Journal BE
// logging in on the same creds. It's the direct evidence of a concurrent session against Journal.
export const CONCURRENT_JOIN_FIELDS: Record<number, DiagField> = {
  1: ['is_same_app_type', 'varint'],
};

// ClientMsg top-level field number → name (webapi_2.proto). Only the fields the BE actually relays are
// mapped; anything else logs as "field N". Used to name WHAT frame got dropped when the browser WS is
// closed — so a "outbound dropped" storm points straight at the offending message type (e.g. Pong).
const CLIENT_MSG_FIELDS: Record<number, string> = {
  100: 'Logon', 101: 'RestoreOrJoinSession', 102: 'Logoff', 107: 'Ping', 108: 'Pong',
  105: 'UserSessionStateRequest', 1: 'InformationRequests', 2: 'TradeSubscriptions',
  3: 'OrderRequests', 4: 'MarketDataSubscriptions', 7: 'TimeAndSalesRequests',
  8: 'TimeBarRequests', 9: 'VolumeProfileRequests', 14: 'NonTimedBarRequests', 10: 'RuleRequests',
};

/**
 * Name the top-level ClientMsg fields present in a relayed frame (wire-level, no proto runtime —
 * sibling to {@link decodeSubmessageFields}). Returns e.g. "Pong" or "TradeSubscriptions,Logon", or
 * "field 23" for an unmapped field. Best-effort: returns "?" on any malformed tag.
 */
export function describeClientMsg(bytes: Uint8Array): string {
  try {
    const names: string[] = [];
    let i = 0;
    while (i < bytes.length) {
      const [tag, ni] = readVarint(bytes, i); if (ni < 0) break; i = ni;
      const field = tag >>> 3, wire = tag & 0x7;
      names.push(CLIENT_MSG_FIELDS[field] ?? `field ${field}`);
      if (wire === 2) { const [len, nl] = readVarint(bytes, i); if (nl < 0) break; i = nl + len; }
      else if (wire === 0) { const [, nj] = readVarint(bytes, i); if (nj < 0) break; i = nj; }
      else if (wire === 1) { i += 8; } else if (wire === 5) { i += 4; } else break;
    }
    return names.length ? [...new Set(names)].join(',') : '?';
  } catch { return '?'; }
}

/**
 * Wire-level decode (no proto runtime — sibling to describeServerMsg) of the length-delimited
 * submessage at top-level `targetField`, extracting the scalar fields named in `fieldMap`. Redacted
 * fields emit `<redacted len=N>`, never their value. Debug-only: returns {} on any malformed tag.
 */
export function decodeSubmessageFields(
  bytes: Uint8Array, targetField: number, fieldMap: Record<number, DiagField>,
): Record<string, string | number> {
  try {
    const sub = findSubmessage(bytes, targetField);
    if (!sub) return {};
    const out: Record<string, string | number> = {};
    const td = new TextDecoder();
    let i = 0;
    while (i < sub.length) {
      const [tag, ni] = readVarint(sub, i); if (ni < 0) break; i = ni;
      const field = tag >>> 3, wire = tag & 0x7;
      const spec = fieldMap[field];
      if (wire === 0) {
        const [val, nj] = readVarint(sub, i); if (nj < 0) break; i = nj;
        if (spec) out[spec[0]] = spec[1] === 'redact' ? '<redacted>' : val;
      } else if (wire === 2) {
        const [len, nl] = readVarint(sub, i); if (nl < 0) break; i = nl;
        const slice = sub.subarray(i, i + len); i += len;
        if (spec) out[spec[0]] = spec[1] === 'redact' ? `<redacted len=${len}>` : td.decode(slice);
      } else if (wire === 1) { i += 8; } else if (wire === 5) { i += 4; } else break;
    }
    return out;
  } catch { return {}; }
}

/** Return the raw bytes of the length-delimited (wire type 2) submessage at top-level `targetField`. */
function findSubmessage(bytes: Uint8Array, targetField: number): Uint8Array | null {
  let i = 0;
  while (i < bytes.length) {
    const [tag, ni] = readVarint(bytes, i); if (ni < 0) return null; i = ni;
    const field = tag >>> 3, wire = tag & 0x7;
    if (wire === 2) {
      const [len, nl] = readVarint(bytes, i); if (nl < 0) return null; i = nl;
      if (field === targetField) return bytes.subarray(i, i + len);
      i += len;
    } else if (wire === 0) { const [, nj] = readVarint(bytes, i); if (nj < 0) return null; i = nj; }
    else if (wire === 1) { i += 8; } else if (wire === 5) { i += 4; } else return null;
  }
  return null;
}

/**
 * Diagnostics (demo/dev only): when an inbound frame is a LogonResult (ServerMsg field 100), print
 * CQG's exact answer — result_code + human text_message + the raw base64 (paste into any proto
 * decoder for a full dump). This is "the exact string/JSON coming back from CQG" for a code-101.
 */
function logLogonResultDiag(summary: string, bytes: Uint8Array): void {
  if (!CQG_DIAG || !summary.includes('LogonResult')) return;
  const r = decodeSubmessageFields(bytes, 100, LOGON_RESULT_FIELDS);
  const code = typeof r.result_code === 'number' ? r.result_code : -1;
  console.log(ts(), '[cqg-relay] ⬇ CQG LogonResult', {
    ...r,
    code: RESULT_CODE_LABEL[code] ?? String(code),
    rawBase64: bytesToBase64(bytes),
  });
}

/**
 * Diagnostics (demo/dev only): decode CQG's AUTHORITATIVE session-close reasons off any inbound frame —
 * LoggedOff (ServerMsg field 102) and UserMessage (field 104). When CQG force-logs-off, evicts a
 * concurrent session (LoggedOff reason REASSIGNED), or rejects a protocol violation (a CRITICAL_ERROR
 * UserMessage), it says so HERE. Logging these means a disconnect is read straight from CQG, not guessed.
 */
function logCloseReasonDiag(bytes: Uint8Array): void {
  if (!CQG_DIAG) return;
  const off = decodeSubmessageFields(bytes, 102, LOGGED_OFF_FIELDS);
  if (Object.keys(off).length) {
    const reason = typeof off.logoff_reason === 'number' ? off.logoff_reason : -1;
    console.log(ts(), '[cqg-relay] ⬇ CQG LoggedOff', {
      ...off,
      reason: LOGOFF_REASON_LABEL[reason] ?? String(reason),
      rawBase64: bytesToBase64(bytes),
    });
  }
  const um = decodeSubmessageFields(bytes, 104, USER_MESSAGE_FIELDS);
  if (Object.keys(um).length) {
    const t = typeof um.message_type === 'number' ? um.message_type : -1;
    console.log(ts(), '[cqg-relay] ⬇ CQG UserMessage', {
      ...um,
      type: USER_MESSAGE_TYPE_LABEL[t] ?? String(t),
      rawBase64: bytesToBase64(bytes),
    });
  }
  const join = decodeSubmessageFields(bytes, 111, CONCURRENT_JOIN_FIELDS);
  if (Object.keys(join).length) {
    console.log(ts(), '[cqg-relay] ⬇ CQG ConcurrentConnectionJoin - ANOTHER connection joined this session', {
      ...join,
      sameAppType: join.is_same_app_type === 1, // proto bool → varint 1/0
      note: 'is_same_app_type=1 ⇒ same private_label+client_app_id (e.g. the deployed Journal BE on the same creds). A LoggedOff reason=REASSIGNED close after this = concurrent-session eviction.',
      rawBase64: bytesToBase64(bytes),
    });
  }
}

/** Decode a base-128 varint at `pos`; returns [value, nextPos] or [0, -1] if truncated. */
function readVarint(bytes: Uint8Array, pos: number): [number, number] {
  let val = 0, shift = 0, i = pos;
  while (i < bytes.length) {
    const b = bytes[i++]!;
    val |= (b & 0x7f) << shift;
    if (!(b & 0x80)) return [val >>> 0, i];
    shift += 7;
    if (shift > 35) return [0, -1];
  }
  return [0, -1];
}

export class CqgRelay {
  private hub: signalR.HubConnection | null = null;
  private socket: WebSocket | null = null;
  private starting: Promise<void> | null = null;
  private unmappedLogCount = 0;

  // Per-socket connect/disconnect diagnostics (demo/dev only). Makes "why did it close?" self-evident:
  // a running sequence #, how many Logons the BE sent on THIS socket, the last LogonResult code, and
  // whether it ever went live. A 2nd Logon on an already-authenticated socket is CQG's duplicate-logon
  // close; a clean remote close after ConcurrentConnectionJoin / LoggedOff(REASSIGNED) is a takeover.
  private socketSeq = 0;
  private socketDiag = { seq: 0, openedAt: 0, logonsSent: 0, lastLogonResultCode: -1, wentLive: false };

  // FE-originated outbound frames (the browser-built market_data_subscription) queued while the
  // socket isn't open yet; flushed on socket.onopen. Everything else outbound is a BE-built frame
  // relayed via CqgSend, and every inbound frame is still forwarded to the BE via CqgInbound —
  // EXCEPT the Logon, which the browser builds + sends itself (sendLogonFirst) and never mirrors.
  private outboundQueue: Uint8Array[] = [];

  // Non-secret logon parameters from the last CqgOpenSocket push (BE CqgOptions → CqgLogonParameters).
  // FAIL-CLOSED (2026-07-18, Demo/Live selector): with per-connect environments these params CAN now
  // change between sessions, so an unusable push must CLEAR the previous set rather than retain it —
  // logging on to demoapi with the retained LIVE app-id pair 101-rejects and looks exactly like bad
  // credentials. sendLogonFirst already refuses to logon while params are null.
  private logonParams: CqgLogonParams | null = null;

  // Actual browser→CQG WebSocket liveness for THIS tab, so the UI can tell "the BE thinks we're
  // live/connecting" apart from "this browser actually has a socket open". After a page refresh the
  // socket is gone but the in-memory BE session may still report live/connecting — without this the
  // UI gets stuck on "Connecting…" with no way to reconnect.
  private connState: RelayConnState = 'closed';
  private connListeners = new Set<() => void>();

  // Listeners for decoded inbound CQG frame summaries (e.g. "TradeSnapshotCompletions",
  // "InformationReports,OrderStatuses×2"). The import-progress UI subscribes to these so it can
  // advance + finish off THIS tab's actual socket — the authoritative, already-connected channel —
  // rather than racing the separate /hub/notifications `cqg.import.complete` event (which the BE
  // publishes ~1s after logon, often before the notifications hub is even subscribed → stuck at 55%).
  private msgListeners = new Set<(summary: string, bytes: number) => void>();

  /** Current browser→CQG socket state for this tab. */
  getConnectionState(): RelayConnState { return this.connState; }

  /** Subscribe to socket-state changes (for useSyncExternalStore / React state). Returns an unsubscribe. */
  onConnectionChange(listener: () => void): () => void {
    this.connListeners.add(listener);
    return () => { this.connListeners.delete(listener); };
  }

  /** Subscribe to decoded inbound CQG frame summaries seen on THIS tab's socket. Returns unsubscribe. */
  onMessage(listener: (summary: string, bytes: number) => void): () => void {
    this.msgListeners.add(listener);
    return () => { this.msgListeners.delete(listener); };
  }

  private emitMessage(summary: string, bytes: number): void {
    this.msgListeners.forEach(l => { try { l(summary, bytes); } catch { /* ignore */ } });
  }

  private setConnState(next: RelayConnState): void {
    if (this.connState === next) return;
    console.log('%s [cqg-relay] browser→CQG socket: %s → %s', ts(), this.connState, next);
    this.connState = next;
    this.connListeners.forEach(l => { try { l(); } catch { /* ignore */ } });
  }
  private beforeUnloadHandler: (() => void) | null = null;

  /** Cross-tab ownership (D1, 2026-09-13): a passive tab never opens a socket; an owner yields to a later claim. */
  private readonly ownership: RelayOwnership;
  private pendingYieldRelease = false;
  private reclaimInFlight = false;

  /** The API task the CURRENT hub connection reported (CqgServerInstance); null until it does. */
  private hubTask: string | null = null;
  private hubTaskWaiters: Array<(task: string) => void> = [];
  /** The in-flight hub alignment, shared by concurrent callers (see core/realtime/hubAffinity). */
  private aligning: Promise<HubAffinityOutcome> | null = null;
  /** One REST call whose response names REST's API task (and carries the ALB sticky cookie). */
  private readonly probeRest: () => Promise<unknown>;

  constructor(
    private readonly getAccessToken: () => Promise<string | null>,
    ownership: RelayOwnership = getRelayOwnership('cqg'),
    probeRest?: () => Promise<unknown>,
  ) {
    this.probeRest = probeRest ?? (() => new ApiClient({ getAccessToken }).get('/api/v1/cqg/status'));
    this.ownership = ownership;
    this.installOwnershipHandlers();
  }

  /** This tab's cross-tab ownership tracker (badge/dialog read it via useRelayOwnership). */
  getOwnership(): RelayOwnership { return this.ownership; }

  /** Forward an app-level line (e.g. `page-reload reason=…`) to the BE client log; console always. */
  clientLog(level: 'info' | 'warn', message: string, detail?: string): void {
    this.log(level, message, detail);
  }

  async start(): Promise<void> {
    console.log(ts(), '[cqg-relay] start() entered', { hubState: this.hub?.state ?? 'null', starting: !!this.starting });
    if (this.hub?.state === signalR.HubConnectionState.Connected) {
      // start() runs right before every connect POST (the dialog's Connect and the silent resume both
      // call it first), and that POST creates the session on whichever task REST lands on. A hub that
      // has been up for a while may no longer share that task (the ALB cookie lapsed while the tab was
      // hidden), so re-check and move the hub first; otherwise the session's frames go elsewhere.
      await this.alignHub('pre-connect');
      console.log(ts(), '[cqg-relay] start() short-circuit - already connected');
      return;
    }
    if (this.starting) {
      console.log(ts(), '[cqg-relay] start() short-circuit - in-flight start, awaiting it');
      return this.starting;
    }

    this.starting = (async () => {
      await this.connectHub();
      // BEFORE anything asks the BE for our session (CqgResume below, or the caller's connect POST): a
      // hub that connected without a live ALB cookie can sit on a different API task than REST, and
      // then every frame it relays is dropped (dev 2026-09-24: 4.5 minutes of a CQG session lost this
      // way). Move it to REST's task first.
      await this.alignHub('hub-start');

      // B3 (2026-09-13): if the previous page reloaded itself, say why — once, from whichever relay
      // hub connects first — so a reload storm is attributable from the BE log alone.
      const boot = consumeBootMarker();
      if (boot) this.log('info', 'cqg-relay boot', describeBoot(boot));

      // Page refresh / fresh load: this browser has no CQG socket yet, but the BE may still hold a
      // Live session whose WS died with the old page. Ask it to resume so the connection renews
      // itself (re-open WS → re-Logon) instead of leaving a "live" badge over a dead socket.
      // (Skipped while latched — a rejected logon, a kick or a user Disconnect must not be silently
      // re-attempted on hub start, even after a reload.)
      this.resumeIfNoSocket('hub-start');

      this.installBeforeUnloadGuard();
    })();

    try {
      await this.starting;
      console.log(ts(), '[cqg-relay] start() resolved');
    } catch (err) {
      console.error(ts(), '[cqg-relay] start() rejected', err);
      throw err;
    } finally {
      this.starting = null;
    }
  }

  /** Ask the BE to resume our session when this tab has no CQG socket (unless latched or passive). */
  private resumeIfNoSocket(trigger: 'hub-start' | 'reconnect'): void {
    if (this.connState === 'open') return;
    const label = trigger === 'hub-start' ? 'cqg-relay hub-start CqgResume suppressed' : 'cqg-relay hub reconnected';
    if (isCqgAutoResumeBlocked()) {
      this.log('info', label, trigger === 'hub-start'
        ? `auto-resume latched (${describeCqgLatch()}); awaiting explicit user Connect`
        : `CqgResume suppressed - auto-resume latched (${describeCqgLatch()}); awaiting explicit user Connect`);
      return;
    }
    if (this.ownership.isPassive()) {
      this.log('info', label, trigger === 'hub-start'
        ? 'another tab owns the CQG connection'
        : 'CqgResume suppressed - another tab owns the CQG connection');
      return;
    }
    const failure = trigger === 'hub-start' ? 'cqg-relay CqgResume invoke failed' : 'cqg-relay CqgResume (reconnect) failed';
    void this.hub?.invoke('CqgResume').catch((err) => this.log('warn', failure, String(err)));
  }

  /**
   * Keep the relay hub on the API task REST lands on (core/realtime/hubAffinity). Single-flight: a
   * second caller (e.g. a Connect click while the hub-start alignment runs) shares the first's result.
   */
  private alignHub(reason: string): Promise<HubAffinityOutcome> {
    if (this.aligning) return this.aligning;
    this.aligning = alignHubWithRest('cqg-relay', {
      probeRest: this.probeRest,
      hubInstance: (timeoutMs) => this.waitHubTask(timeoutMs),
      reconnectHub: () => this.reconnectHub(),
      log: (level, message, detail) => this.log(level, message, detail),
    }, reason).finally(() => { this.aligning = null; });
    return this.aligning;
  }

  /** The task the current hub connection reported, waiting up to `timeoutMs` for its push. */
  private waitHubTask(timeoutMs: number): Promise<string | null> {
    if (this.hubTask) return Promise.resolve(this.hubTask);
    return new Promise((resolve) => {
      const done = (task: string) => { clearTimeout(timer); resolve(task); };
      const timer = setTimeout(() => {
        this.hubTaskWaiters = this.hubTaskWaiters.filter((w) => w !== done);
        resolve(null);
      }, timeoutMs);
      this.hubTaskWaiters.push(done);
    });
  }

  /** Replace ONLY the hub connection. The browser→CQG socket is untouched. */
  private async reconnectHub(): Promise<void> {
    const old = this.hub;
    this.hub = null;
    this.hubTask = null;
    if (old) { try { await old.stop(); } catch { /* ignore */ } }
    await this.connectHub();
  }

  /** Build the relay hub, wire its handlers, and connect it (sets `this.hub`). */
  private async connectHub(): Promise<void> {
    const httpOptions: signalR.IHttpConnectionOptions = {};
    if (devAuthUser) {
      httpOptions.transport = signalR.HttpTransportType.LongPolling;
      httpOptions.headers = { [DEV_USER_HEADER]: devAuthUser };
      console.log(ts(), '[cqg-relay] start() using LongPolling + dev-auth header');
    } else {
      httpOptions.transport = signalR.HttpTransportType.WebSockets;
      // skipNegotiation: one WS request instead of negotiate+connect — required for correct
      // connects once the API runs >1 task behind the ALB (see hubClient.ts for the full note).
      httpOptions.skipNegotiation = true;
      httpOptions.accessTokenFactory = async () => (await this.getAccessToken()) ?? '';
      console.log(ts(), '[cqg-relay] start() using WebSocket (skipNegotiation) + cognito accessTokenFactory');
    }

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(`${env.apiBaseUrl}/hub/cqg`, httpOptions)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    hub.on('CqgOpenSocket', (url: string, logonParams?: unknown) => {
      const params = normalizeLogonParams(logonParams);
      console.log(ts(), '[cqg-relay] hub → CqgOpenSocket', { url, hasLogonParams: !!params });
      // Fail closed: an unusable push CLEARS the previous set instead of retaining it — params are
      // per-ENVIRONMENT now (Demo/Live), and a stale pair on a new gateway 101-rejects in a way
      // that's indistinguishable from bad credentials. No params → sendLogonFirst stays quiet.
      this.logonParams = params;
      if (!params) this.log('error', 'cqg-relay CqgOpenSocket carried no usable logon parameters', 'FE cannot build the Logon without them; cleared the previous set (fail closed)');
      this.openSocket(url);
    });
    hub.on('CqgCloseSocket', () => {
      console.log(ts(), '[cqg-relay] hub → CqgCloseSocket');
      this.closeSocket();
    });
    hub.on('CqgSend', (payload: ArrayBuffer | Uint8Array | string) => {
      // SignalR's JSON protocol base64-encodes server-sent byte[] into a string on the wire.
      // MessagePack protocol would keep it binary; until we switch, decode the string here.
      const bytes = this.coerceToBytes(payload);
      console.log(ts(), '[cqg-relay] hub → CqgSend', {
        rawType: typeof payload,
        isString: typeof payload === 'string',
        decodedBytes: bytes.length,
      });
      // Diagnostics (demo/dev only): if this is an outbound Logon (should never happen since the
      // FE builds the Logon itself — a BE-relayed one would be a regression), decode + flag it.
      this.logOutboundLogonDiag(bytes);
      this.sendToCqg(bytes);
    });

    // Which API task holds THIS hub, and does it have our session? CQG sessions live in per-task
    // memory just like Rithmic's, so a hub on a different task than the connect POST silently drops
    // every relayed frame. Recorded for the hub-affinity check (which moves the hub), and logged so
    // the split stays visible (see core/api/serverInstance.ts).
    hub.on('CqgServerInstance', (instanceId: string, hasSession: boolean) => {
      _hubInstanceId = instanceId ?? null;
      this.hubTask = instanceId ?? null;
      if (this.hubTask) {
        const waiters = this.hubTaskWaiters;
        this.hubTaskWaiters = [];
        waiters.forEach((w) => w(this.hubTask!));
      }
      const rest = lastRestInstanceId();
      this.log('info', 'cqg-relay hub instance',
        `hubTask=${instanceId} hubHasSession=${hasSession} lastRestTask=${rest ?? 'n/a'} restTasksSeen=[${seenRestInstanceIds().join(',')}]`);
      if (rest && instanceId && rest !== instanceId) {
        this.log('warn', 'cqg-relay ⚠ TASK SPLIT',
          `the relay hub is on API task ${instanceId} but REST calls are hitting ${rest} - this hub cannot ` +
          'see the session the connect POST created and would DROP relayed frames. The hub-affinity check ' +
          'moves the hub to the REST task.');
      }
    });

    // The BE has no session for us (e.g. the pod rolled mid-session) and is dropping our relayed
    // frames — it asks us to re-establish. Debounced so the per-dropped-frame flood collapses into
    // ONE call; the registered handler (the dialog) silently POSTs a CREDENTIAL-LESS /cqg/connect —
    // the BE re-arms and pushes CqgOpenSocket, and this relay logons itself from stored creds at
    // socket-open (2026-07-08 decision: blips auto-reconnect silently; only a concurrent-session
    // conflict — CQG code 105, since the silent logon never sets drop_concurrent_session — prompts).
    hub.on('CqgReconnectNeeded', () => {
      if (isCqgAutoResumeBlocked()) {
        this.log('info', 'cqg-relay hub → CqgReconnectNeeded',
          `suppressed - auto-resume latched (${describeCqgLatch()}); awaiting explicit user Connect`);
        return;
      }
      if (this.ownership.isPassive()) {
        this.log('info', 'cqg-relay hub → CqgReconnectNeeded',
          `suppressed - another tab owns the CQG connection (owner=${this.ownership.snapshot().ownerTabId ?? '?'})`);
        return;
      }
      const now = Date.now();
      if (now - _lastReconnectRequestAt < 3000) return;
      _lastReconnectRequestAt = now;
      this.log('info', 'cqg-relay hub → CqgReconnectNeeded',
        `BE has no session; re-establishing via credential-less connect :: hubTask=${_hubInstanceId ?? 'unknown'} ` +
        `lastRestTask=${lastRestInstanceId() ?? 'n/a'} restTasksSeen=[${seenRestInstanceIds().join(',')}]`);
      _reconnectHandler?.();
    });

    // A reconnect is a NEW WebSocket, routed like a fresh one: until it reports its task, it has none.
    hub.onreconnecting(() => { if (this.hub === hub) this.hubTask = null; });

    // If SignalR drops and auto-reconnects while this browser's CQG socket also died (network
    // blip, laptop sleep), the BE may still hold a Live session over a dead WS. Ask it to resume
    // so the connection renews itself rather than going silently dark. First put the reconnected hub
    // back on REST's task (a wake after an hour idle has no live ALB cookie, so the reconnect was
    // routed on its own).
    hub.onreconnected(() => {
      this.log('info', 'cqg-relay hub reconnected', `wsState=${this.connState}`);
      void this.alignHub('hub-reconnected').then(() => this.resumeIfNoSocket('reconnect'));
    });

    console.log(ts(), '[cqg-relay] hub.start() awaiting…');
    this.hubTask = null;
    try {
      await hub.start();
    } catch (err) {
      console.error(ts(), '[cqg-relay] hub.start() threw', err);
      throw err;
    }
    this.hub = hub;
    this.log('info', 'cqg-relay hub connected', `state=${hub.state}`);
  }

  async stop(): Promise<void> {
    this.log('info', 'cqg-relay stop requested');
    this.uninstallBeforeUnloadGuard();
    this.ownership.release();
    this.setConnState('closed');
    try {
      this.socket?.close();
    } catch {
      /* ignore */
    }
    this.socket = null;

    if (this.hub) {
      try {
        await this.hub.stop();
      } catch {
        /* ignore */
      }
      this.hub = null;
    }
    this.hubTask = null;
  }

  private openSocket(url: string): void {
    // Cross-tab ownership (D1): the BE pushes CqgOpenSocket to EVERY tab of the user; only the tab that
    // claimed the connection may act on it — a passive tab opening its own socket is the two-context
    // fight behind `LoggedOff FORCED "concurrent session"` + "Too many failed" (prod 2026-09-12).
    if (this.ownership.isPassive()) {
      this.log('info', 'cqg-relay openSocket ignored - another tab owns the CQG connection',
        `owner=${this.ownership.snapshot().ownerTabId ?? '?'} url=${url}`);
      return;
    }
    // Idempotent. Duplicate CqgOpenSocket messages arrive on page load when the hub-start resume
    // (CqgResume) races the auto-resume /cqg/connect — both ask the browser to open a socket. We must
    // NOT tear down an in-flight/open socket to reopen: closing a still-CONNECTING WebSocket kills it
    // with code 1006 ("closed before the connection is established"), and that dead socket's async
    // onclose then nulls out its replacement + tells the BE the relay closed, so the Logon bytes get
    // dropped and the session never goes live. If a socket is already open or connecting, ignore.
    if (this.socket &&
        (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      this.log('info', 'cqg-relay openSocket ignored - already open/opening',
        `url=${url} readyState=${this.socket.readyState}`);
      return;
    }

    // FE-owned credentials gate: without stored creds the browser can't logon, and an
    // unauthenticated CQG socket is useless (CQG closes it after the logon timeout) — don't open
    // one. The user must Connect explicitly (which stores creds BEFORE the POST). The BE session
    // parks in Connecting; the hub's OnConnectedAsync replay re-pushes CqgOpenSocket after the
    // user's next connect.
    const gateCreds = readStoredCqgCreds();
    if (!gateCreds.username || !gateCreds.password) {
      this.log('warn', 'cqg-relay openSocket ignored - no stored CQG credentials', 'user must Connect explicitly');
      return;
    }

    this.log('info', 'cqg-relay opening browser WebSocket', `url=${url}`);
    // Any lingering socket here is CLOSING/CLOSED; its lifecycle handlers are guarded by the identity
    // check below, so a late event from it can't clobber the new socket.
    if (this.socket) {
      try {
        this.socket.close();
      } catch {
        /* ignore */
      }
    }

    const socket = new WebSocket(url);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    this.setConnState('opening');
    if (CQG_DIAG) {
      this.socketSeq += 1;
      this.socketDiag = { seq: this.socketSeq, openedAt: Date.now(), logonsSent: 0, lastLogonResultCode: -1, wentLive: false };
      console.log(ts(), '[cqg-relay] browser WS opening (session diag)', { socketSeq: this.socketSeq, url });
    }

    socket.onopen = () => {
      if (this.socket !== socket) return; // superseded by a newer socket — ignore
      // Fresh CQG socket = a NEW session. CQG reuses contractIds across sessions for DIFFERENT
      // instruments, so the decoder's prior-session contract→symbol/scale map is now stale and could
      // mis-scale a quote on a reused id (the ×100 phantom open-P&L). Reset it BEFORE we flip to
      // 'open' (which triggers the subscriber's re-flush): CQG re-sends resolutions this session and
      // the subscriber re-requests everything. Mirrors the BE's per-session generation bump.
      resetContractResolution();
      // A new session = a clean live-positions store. The user's directive: don't trust what the FE
      // cached before the refresh — CQG re-sends the FULL position snapshot immediately on connect, so
      // we wipe and let the broker's truth repopulate it. The decoder's per-(account, contract) LOT
      // maps reset with it: contract ids re-mint per session, and the fresh is_snapshot=true frames
      // re-baseline the lots.
      livePositionsStore.reset();
      resetPositionLots();
      // Re-arm the position-snapshot gate: CQG re-sends the on-connect position snapshot BEFORE the
      // next TradeSnapshotCompletions. Until that completion the merge falls back to the BE /positions
      // rows verbatim (don't blank the user mid-connect); after it, CQG's live set is authoritative.
      resetPositionSnapshot();
      // LOGON FIRST (2026-07-08: the browser owns the CQG credentials — the BE never sees them).
      // CQG requires the Logon to be the FIRST frame on a fresh socket, so it goes out
      // synchronously BEFORE the conn-state flip (which re-flushes the quote subscriber's frames)
      // and BEFORE the outbound-queue flush. Sent DIRECTLY on the socket — never via sendClient,
      // whose session-tape mirror must never carry a session-level frame.
      this.sendLogonFirst(socket);
      this.setConnState('open');
      this.log('info', 'cqg-relay browser WebSocket open', `url=${url}`);
      void this.hub?.invoke('CqgRelayOpened').catch((err) => {
        this.log('error', 'cqg-relay CqgRelayOpened invoke failed', String(err));
      });
      // FE-built frames queued in `outboundQueue` stay queued until CQG acks the Logon (`live`, see
      // onLogonResultFrame) — anything written between the Logon and its ack is answered
      // `101 You are not logged in` and silently lost (2026-09-13).
    };

    socket.onmessage = (event) => {
      if (this.socket !== socket) return; // stale socket — ignore
      const data = event.data;
      if (data instanceof ArrayBuffer) {
        const bytes = new Uint8Array(data);
        const summary = describeServerMsg(bytes);
        // Pure market-data ticks arrive many times per second — logging them drowns out every other
        // line (and each log round-trips to the BE via CqgClientLog). Log everything EXCEPT them.
        if (!isPureMarketData(summary)) this.log('debug', 'cqg-relay inbound binary frame', `${summary} bytes=${bytes.length}`);
        logLogonResultDiag(summary, bytes);
        logCloseReasonDiag(bytes);      // CQG's authoritative LoggedOff / UserMessage / ConcurrentJoin
        this.trackSessionDiag(summary, bytes);
        this.onLogonResultFrame(socket, summary, bytes);
        this.emitMessage(summary, bytes.length);
        // Position-snapshot gate: the first TradeSnapshotCompletions (ServerMsg field 4) marks the end
        // of the on-connect snapshot. From here CQG's live position set is authoritative for the merge
        // (before it, the merge falls back to the BE /positions rows verbatim).
        if (summary.includes('TradeSnapshotCompletions')) markPositionSnapshotReady();
        // Decode quotes/contract-metadata straight off the socket into liveQuotesStore so the live
        // components tick directly from CQG — no BE round-trip, survives BE restarts. Additive:
        // the bytes are still forwarded to the BE below for orders/positions/history.
        const dec = decodeAndApplyServerMsg(bytes);
        if (dec.newResolutions.length) this.log('info', 'cqg-decode resolved', dec.newResolutions.join(', '));
        if (dec.firstQuotes.length)    this.log('info', 'cqg-decode first quote', dec.firstQuotes.join(', '));
        if (dec.unmappedContractIds.length && this.unmappedLogCount++ < 8)
          this.log('warn', 'cqg-decode quote for unmapped contract', dec.unmappedContractIds.join(','));
        // LIVE OPEN POSITIONS: decode CQG's authoritative PositionStatuses off the same frame so the
        // open-positions list reflects exactly what the broker says is open (no fill reconstruction =
        // no phantom "double line" on refresh). Additive — the bytes still go to the BE below.
        const posDec = decodeAndApplyPositions(bytes);
        if (posDec.applied.length) this.log('info', 'cqg-decode live position', posDec.applied.join(', '));
        // t0 of the live-update flow: stamp when a recompute-triggering frame arrives in the browser
        // (opt-in: DEV, or localStorage.cqgPerf='1'). Pair with the [realtime] e2eMs log downstream.
        if (summary.includes('OrderStatuses') && (import.meta.env.DEV || (typeof localStorage !== 'undefined' && localStorage.getItem('cqgPerf') === '1')))
          console.log('%s [cqg-relay] orderStatuses frame in @t=%s (%s)', ts(), performance.now().toFixed(0), summary);
        // SignalR JSON protocol turns Uint8Array into {"0":n,"1":n,...} on the wire which the
        // server can't bind to byte[]. Pre-encode as base64 — System.Text.Json then decodes
        // the JSON string into byte[] natively.
        const b64 = bytesToBase64(bytes);
        void this.hub?.invoke('CqgInbound', b64).catch((err) => {
          this.log('error', 'cqg-relay CqgInbound invoke failed', String(err));
        });
      } else if (data instanceof Blob) {
        // Older Safari may deliver Blob even with binaryType='arraybuffer'; coerce.
        void data.arrayBuffer().then((buf) => {
          const bytes = new Uint8Array(buf);
          const summary = describeServerMsg(bytes);
          if (!isPureMarketData(summary)) this.log('debug', 'cqg-relay inbound binary frame (via Blob)', `${summary} bytes=${bytes.length}`);
          logLogonResultDiag(summary, bytes);
          this.onLogonResultFrame(socket, summary, bytes);
          this.emitMessage(summary, bytes.length);
          if (summary.includes('TradeSnapshotCompletions')) markPositionSnapshotReady();
          decodeAndApplyServerMsg(bytes);
          decodeAndApplyPositions(bytes);
          const b64 = bytesToBase64(bytes);
          void this.hub?.invoke('CqgInbound', b64);
        });
      } else {
        this.log('warn', 'cqg-relay unexpected text frame from CQG; dropping', String(data));
      }
    };

    socket.onerror = (event) => {
      if (this.socket !== socket) return; // stale socket — ignore
      this.log('error', 'cqg-relay browser WebSocket error', JSON.stringify({
        readyState: socket.readyState,
        type: (event as Event).type,
      }));
    };

    socket.onclose = (event) => {
      if (this.socket !== socket) return; // a superseded socket closing must not touch current state
      this.setConnState('closed');
      this.log('info', 'cqg-relay browser WebSocket closed',
        `code=${event.code} reason=${event.reason || '<none>'} wasClean=${event.wasClean}`);
      if (CQG_DIAG) console.log(ts(), '[cqg-relay] ⬇ browser WS closed (session diag)', {
        code: event.code, reason: event.reason || '<none>', wasClean: event.wasClean,
        socketSeq: this.socketDiag.seq,
        logonsSentOnThisSocket: this.socketDiag.logonsSent,
        lastLogonResultCode: this.socketDiag.lastLogonResultCode,
        wentLive: this.socketDiag.wentLive,
        msOpen: this.socketDiag.openedAt ? Date.now() - this.socketDiag.openedAt : null,
        // Factual pointer, NOT a guess: CQG states the reason in the ⬇ CQG LoggedOff / UserMessage /
        // ConcurrentConnectionJoin lines above. code=1000 wasClean=true ⇒ CQG closed cleanly (e.g.
        // duplicate logon / takeover); 1006 ⇒ abnormal (never established / network).
        readReasonFrom: 'the ⬇ CQG LoggedOff / UserMessage / ConcurrentConnectionJoin lines just above',
      });
      void this.hub?.invoke('CqgRelayClosed', event.code, event.reason || '').catch(() => {});
      this.socket = null;
      if (this.pendingYieldRelease) this.finishYield();
    };
  }

  private closeSocket(): void {
    this.log('info', 'cqg-relay close requested by server');
    this.setConnState('closed');
    if (this.socket) {
      try {
        this.socket.close(1000, 'server requested close');
      } catch {
        /* ignore */
      }
      this.socket = null;
    }
  }

  /**
   * Send a browser-built CQG frame (the market_data_subscription — live-market-data is FE-owned).
   * If the socket isn't open yet, queue it and flush on open. This is the ONLY browser-originated
   * outbound; it is never routed to the BE as traffic (CqgInbound is inbound-only). All other CQG
   * traffic is unchanged: BE-built frames relay via CqgSend, and every inbound frame goes to the BE.
   */
  sendClient(bytes: Uint8Array): void {
    // Session-tape mirror (dev/demo builds only — prod pays nothing): FE-originated frames go
    // straight over the browser socket, so the BE's frame recorder never sees them. Mirror a copy
    // via CqgFeFrame so the recorded tape is a COMPLETE session (see docs/integrations/cqg.md,
    // "session tape"). CREDENTIAL REDACTION: session-level frames (top-level field >= 100 — the
    // Logon carries the plaintext password) are NEVER mirrored; the BE recorder drops them too
    // (defense in depth). Fire-and-forget; the BE no-ops unless Cqg:FrameRecordingEnabled.
    if (import.meta.env.MODE !== 'prod'
      && this.hub && this.hub.state === signalR.HubConnectionState.Connected
      && !hasSessionLevelField(bytes)) {
      void this.hub.invoke('CqgFeFrame', bytes).catch(() => { /* the tape is best-effort */ });
    }
    // LIVE-GATED (2026-09-13): a frame written before CQG acks the Logon is answered
    // `101 You are not logged in` and the request is lost — so FE-built frames wait for `live`
    // (LogonResult result_code 0 on THIS socket), not merely for the WebSocket to be open.
    if (this.connState === 'live' && this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.sendToCqg(bytes);
    } else {
      this.outboundQueue.push(bytes);
    }
  }

  /**
   * The Logon's verdict on the CURRENT socket. result_code 0 ⇒ `live`: flush the FE frames queued
   * since the socket opened (quote subscribes / symbol resolutions). Any other code leaves the
   * state at `open` — the BE publishes the rejection as `cqg.state`, and the queue drains on the
   * next successful logon (or is dropped with the socket).
   */
  private onLogonResultFrame(socket: WebSocket, summary: string, bytes: Uint8Array): void {
    if (this.socket !== socket) return;
    const code = parseLogonResultCode(summary, bytes);
    if (code === null) return;
    if (code !== 0) {
      // CQG refused THESE credentials on THIS socket — latch here, not only on the BE's `cqg.state`
      // error push. When the BE is unreachable (the hub and the connect POST on different API tasks —
      // the `⚠ TASK SPLIT` line) that push never arrives, and every CqgReconnectNeeded re-sent the same
      // refused credentials: dev 2026-09-14 06:44–06:50, eight `result_code=101` logons in five minutes.
      // The Rithmic relay has always latched on its own refused ResponseLogin; this makes CQG symmetric.
      // 105 is CQG's CONCURRENT_SESSION (its own meaning, not an invented mapping) — the take-over shape
      // of a kick, which the BE reports as reason=concurrent_session.
      blockCqgAutoResume(code === RESULT_CODE_CONCURRENT_SESSION ? 'kicked' : 'logon-rejected');
      this.log('warn', 'cqg-relay LogonResult refused - FE frames stay queued',
        `result_code=${code} queued=${this.outboundQueue.length} auto-resume latched (${describeCqgLatch()})`);
      this.reclaimInFlight = false;
      this.ownership.failed();
      return;
    }
    this.setConnState('live');
    // An accepted logon ends the reason for any latch — the kick is history, the credentials work.
    // Without this the durable latch outlived the session it was about: dev 2026-09-14 09:42, a page
    // load still refused to resume while latched `kicked`, minutes after two successful logons.
    clearCqgLatchAfterAcceptedLogon();
    this.reclaimInFlight = false;
    this.ownership.connected();
    const pending = this.outboundQueue;
    this.outboundQueue = [];
    this.log('info', 'cqg-relay LogonResult ok - logon acked, flushing FE frames', `queued=${pending.length}`);
    for (const frame of pending) this.sendToCqg(frame);
  }

  /**
   * Build + send the CQG Logon as the FIRST frame on a freshly-opened socket (2026-07-08: the
   * browser owns the credentials; they never transit our BE). Reads creds from localStorage
   * (guaranteed present — openSocket gates on them), takes the logon parameters from the last
   * CqgOpenSocket push, and consumes the one-shot take-over flag. Sent with socket.send directly:
   * this bypasses sendClient (whose session-tape mirror must never carry a Logon) and sendToCqg's
   * outbound logging. The BE log only learns non-secret facts (appId, dropConcurrent).
   */
  private sendLogonFirst(socket: WebSocket): void {
    const creds = readStoredCqgCreds();
    if (!creds.username || !creds.password) {
      // Unreachable via openSocket (it gates on creds), but a cleared-storage race must not throw.
      this.log('warn', 'cqg-relay logon skipped - no stored credentials', 'user must Connect explicitly');
      return;
    }
    const params = this.logonParams;
    if (!params) {
      this.log('error', 'cqg-relay logon skipped - no logon parameters', 'CqgOpenSocket push carried none; BE/FE version mismatch?');
      return;
    }
    // One-shot — silent resumes must DETECT a concurrent session (CQG 105 → prompt), never kick it.
    const dropConcurrent = consumeNextLogonDropConcurrent();
    const bytes = buildLogonFrame(creds, params, dropConcurrent);
    this.logOutboundLogonDiag(bytes);
    socket.send(bytes);
    this.log('info', 'cqg-relay ⬆ Logon sent (FE-built)',
      `appId=${params.clientAppId} dropConcurrent=${dropConcurrent} bytes=${bytes.length}`);
  }

  /**
   * Diagnostics (demo/dev only): decode an outbound Logon (ClientMsg field 100) to the browser
   * console — client_app_id/user_name etc., password REDACTED. Console-only (never CqgClientLog —
   * the user_name must not reach the BE log). Also feeds the duplicate-logon close diagnostics.
   */
  private logOutboundLogonDiag(bytes: Uint8Array): void {
    if (!CQG_DIAG) return;
    const logon = decodeSubmessageFields(bytes, 100, LOGON_REQUEST_FIELDS);
    if (!Object.keys(logon).length) return;
    this.socketDiag.logonsSent += 1;
    const alreadyAuthed = this.socketDiag.wentLive || this.socketDiag.lastLogonResultCode === 0;
    console.log(ts(), '[cqg-relay] ⬆ CQG Logon', {
      ...logon,
      socketSeq: this.socketDiag.seq,
      logonsSentOnThisSocket: this.socketDiag.logonsSent,
      socketAlreadyAuthenticated: alreadyAuthed,
      note: alreadyAuthed
        ? '⚠ DUPLICATE LOGON on an already-authenticated socket - CQG closes on a 2nd logon (protocol: logon must be the first & only message after connect)'
        : (this.socketDiag.logonsSent > 1 ? '⚠ 2nd+ logon on this socket (a prior logon did not succeed)' : 'first logon on this socket'),
    });
  }

  /** Update per-socket session diag from inbound frames (demo/dev only) — feeds the duplicate-logon /
   *  close diagnostics. Records the last LogonResult code and whether this socket ever went live. */
  private trackSessionDiag(summary: string, bytes: Uint8Array): void {
    if (!CQG_DIAG || !summary.includes('LogonResult')) return;
    const r = decodeSubmessageFields(bytes, 100, LOGON_RESULT_FIELDS);
    const code = typeof r.result_code === 'number' ? r.result_code : -1;
    this.socketDiag.lastLogonResultCode = code;
    if (code === 0) this.socketDiag.wentLive = true;
  }

  private sendToCqg(payload: ArrayBuffer | Uint8Array | string): void {
    const bytes = this.coerceToBytes(payload);
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.log('warn', 'cqg-relay outbound dropped - browser WS not open',
        `type=${describeClientMsg(bytes)} wsState=${this.socket?.readyState ?? 'null'} bytes=${bytes.length}`);
      return;
    }
    this.log('debug', 'cqg-relay outbound binary frame', `bytes=${bytes.length}`);
    this.socket.send(bytes);
  }

  /**
   * SignalR's JSON protocol turns server-sent byte[] into base64 strings on the wire. The hub
   * methods on @microsoft/signalr deliver the raw deserialized value (a string), not the
   * byte[] the C# method signature implies. Until we adopt MessagePack on both sides, this
   * helper accepts whichever shape arrives and yields a Uint8Array.
   */
  private coerceToBytes(payload: ArrayBuffer | Uint8Array | string): Uint8Array {
    if (typeof payload === 'string') {
      const binary = atob(payload);
      const out = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
      return out;
    }
    if (payload instanceof Uint8Array) return payload;
    return new Uint8Array(payload);
  }

  /**
   * Tab-close cleanup. The browser owns the WS to CQG; if the tab dies without explicitly
   * calling stop() (user closes the tab, navigates away), the WS hangs around until CQG reaps
   * it on idle. We attach a `beforeunload` listener that synchronously closes the browser WS and
   * fires a best-effort hub invocation so the BE can transition to Disconnected immediately
   * instead of waiting for the SignalR connection to time out.
   */
  private installBeforeUnloadGuard(): void {
    if (typeof window === 'undefined') return;
    if (this.beforeUnloadHandler) return;
    const handler = () => {
      // Only a tab that actually holds the broker socket reports the close: the BE session is per USER,
      // so a passive tab's RelayClosed would flip the OWNER's live session to Disconnected and gate its
      // sends (dev 2026-09-13 16:56Z: a passive tab's sign-out → "CQG browser relay closed … page unload"
      // → the owner's next Pong was suppressed and CQG dropped the live session).
      const hadSocket = this.socket !== null;
      this.setConnState('closed');
      try { this.socket?.close(1001, 'page unload'); } catch { /* ignore */ }
      this.socket = null;
      // hub.stop() returns a promise we cannot await during unload, but invoke() schedules the
      // outbound frame on the existing WebSocket; usually that's enough to land server-side
      // before the connection is torn down.
      if (hadSocket) {
        try { void this.hub?.invoke('CqgRelayClosed', 1001, 'page unload'); } catch { /* ignore */ }
      }
      this.ownership.release();
    };
    window.addEventListener('beforeunload', handler);
    this.beforeUnloadHandler = handler;
  }

  private uninstallBeforeUnloadGuard(): void {
    if (typeof window === 'undefined') return;
    if (!this.beforeUnloadHandler) return;
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.beforeUnloadHandler = null;
  }

  /**
   * Log-only single-tab detection. On start(), we broadcast `claim`; any other tab listening on
   * the same channel logs a warning. Doesn't force a release (v2) — the BE-side per-user
   * SignalR group still routes inbound CQG frames to whichever tab last opened the browser WS,
   * so the user is likely to see flicker; the log line is the diagnostic breadcrumb.
   */
  /**
   * Cross-tab ownership hooks (D1, 2026-09-13 — "the last tab to ask is the one that gets connected").
   * `yield`: another tab claimed the CQG connection — close ours cleanly (1000), let the BE hear
   * `CqgRelayClosed` from the socket's onclose, THEN `release()` so the claimant's `/connect` lands on a
   * session that already saw our socket go. `reclaim`: the tab that displaced us failed its logon —
   * one silent reconnect from here, never a loop. `owner-gone`: the owner released (Disconnect / tab
   * close) — a passive tab with stored creds resumes once. Both resumes honour the D4 latch.
   */
  private installOwnershipHandlers(): void {
    this.ownership.onEvent((event) => {
      if (event.type === 'yield') {
        this.log('info', 'cqg-relay yielding the CQG connection to another tab', `owner=${event.toTabId}`);
        if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
          this.pendingYieldRelease = true;
          try { this.socket.close(1000, `yielded to tab ${event.toTabId}`); } catch { this.finishYield(); }
        } else {
          this.finishYield();
        }
        return;
      }
      if (event.type === 'reclaim' || event.type === 'owner-gone') {
        const from = event.type === 'reclaim' ? event.fromTabId : event.tabId;
        // A reclaim may run past a `logon-rejected` latch: that latch was just written by the tab whose
        // logon failed, and the creds are shared browser-wide — the displaced owner gets ONE attempt
        // ("the last tab wins if its connection succeeds"). A kick, a user Disconnect or an exhausted
        // ladder still stand.
        const latch = readAutoResumeLatch('cqg');
        const blocked = event.type === 'reclaim'
          ? (latch !== null && latch !== 'logon-rejected')
          : isCqgAutoResumeBlocked();
        if (blocked) {
          this.log('info', `cqg-relay ${event.type} - not resuming`, `auto-resume latched (${latch ?? 'this-tab'}); tab=${from}`);
          return;
        }
        if (this.reclaimInFlight) return;
        this.reclaimInFlight = true;
        this.log('info', `cqg-relay ${event.type} - resuming from this tab once`, `tab=${from}`);
        _reconnectHandler?.();
      }
    });
  }

  /** The yield handshake's last step: our socket is gone, tell the other tabs (and the claimant). */
  private finishYield(): void {
    this.pendingYieldRelease = false;
    this.ownership.release();
  }

  /**
   * Forward a log line to the BE (and the browser console). Best-effort: if the hub isn't
   * connected yet (e.g. very first start() log), the BE invoke is silently dropped — the
   * console.log still happens, so nothing is truly lost.
   */
  private log(level: 'info' | 'warn' | 'error' | 'debug', message: string, detail?: string): void {
    const consoleLine = detail ? `${message} :: ${detail}` : message;
    if (level === 'error') console.error(ts(), '[cqg-relay]', consoleLine);
    else if (level === 'warn') console.warn(ts(), '[cqg-relay]', consoleLine);
    else if (level === 'debug') console.debug(ts(), '[cqg-relay]', consoleLine);
    else console.log(ts(), '[cqg-relay]', consoleLine);

    const hub = this.hub;
    if (hub && hub.state === signalR.HubConnectionState.Connected) {
      void hub.invoke('CqgClientLog', level, message, detail ?? null).catch(() => {});
    }
  }
}

// ─── App-level singleton ─────────────────────────────────────────────────────
// One relay for the whole app. The connect dialog is rendered in BOTH the Topbar and the
// AccountDropdown, and the state badge reads the same session — they must share ONE relay so a page
// load opens exactly one hub + one CQG socket (not 2-3 fighting each other). The access-token getter
// is injected via a module ref so the singleton always uses the latest auth without being re-created.
let _singleton: CqgRelay | null = null;
// B3: `page-reload reason=…` lines ride this relay's client log (→ BE `[cqg-fe]`) when it exists.
registerReloadLogSink((level, message, detail) => _singleton?.clientLog(level, message, detail));
let _tokenGetter: () => Promise<string | null> = async () => null;

/** Keep the singleton's access-token source current (called from the dialog on every render). */
export function setCqgTokenGetter(fn: () => Promise<string | null>): void {
  _tokenGetter = fn;
}

let _reconnectHandler: (() => void) | null = null;
let _lastReconnectRequestAt = 0;
/** API task id holding the CQG relay hub (from the CqgServerInstance push) — diagnostics only. */
let _hubInstanceId: string | null = null;

/**
 * Register what to do when the BE signals `CqgReconnectNeeded` (it has no session for us, e.g. after a
 * pod roll, and is dropping our frames). The dialog sets this to a SILENT credential-less
 * POST /cqg/connect — the BE re-arms + pushes CqgOpenSocket and the relay logons itself from stored
 * creds at socket-open — so frames stop getting dropped (→ no more CQG↔FIFO mismatches) without a
 * user click. The hub handler debounces the per-frame flood into a single call.
 */
export function setCqgReconnectHandler(fn: () => void): void {
  _reconnectHandler = fn;
}

// ── Terminal-rejection latch ────────────────────────────────────────────────────────────────────
// A code-101 logon rejection (bad username/password) is TERMINAL — the BE pushes cqg.state='error'.
// Without this, three auto-resume paths (CqgReconnectNeeded, hub-onreconnected→CqgResume, hub-start
// CqgResume) kept re-sending the SAME rejected creds on every SignalR blip, hammering CQG hundreds of
// times. When latched, all auto-resume is suppressed until the user EXPLICITLY re-Connects (which
// calls allowCqgAutoResume). A transient 'disconnected'/dead-pipe 'live' never latches, so normal
// pod-roll / network-blip recovery is preserved.
// Since 2026-09-13 (D4) a rejection, a kick (LoggedOff FORCED/REASSIGNED → reason kicked /
// concurrent_session) and the user's Disconnect also latch DURABLY (`@core/realtime/autoResumeLatch`,
// localStorage: every tab, across reloads) — a reload used to forget the latch and auto-resume straight
// back into the kick, which is how CQG reached "Too many failed" on 2026-09-12. A TRANSPORT error
// (reason idle_timeout/transport) latches this tab only (`reason: null`), exactly as before.
let _autoResumeBlocked = false;

/**
 * Latch auto-resume off. With a reason it is durable (every tab + reload); `null` keeps the
 * 2026-08-25 transport behaviour — this tab only, so a reload still self-heals a dead pipe.
 */
export function blockCqgAutoResume(reason: AutoResumeLatchReason | null = 'logon-rejected'): void {
  _autoResumeBlocked = true;
  if (reason) latchAutoResume('cqg', reason);
}

/** Clear both latches — called on an explicit user Connect/Take-over (a deliberate retry). */
export function allowCqgAutoResume(): void {
  _autoResumeBlocked = false;
  releaseAutoResume('cqg');
}

/** Whether auto-resume is currently suppressed — by this tab's latch or the durable one. */
export function isCqgAutoResumeBlocked(): boolean {
  return _autoResumeBlocked || readAutoResumeLatch('cqg') !== null;
}

/** The durable latch reason for log lines ("this-tab" = only the in-memory latch is set). */
function describeCqgLatch(): string {
  return readAutoResumeLatch('cqg') ?? 'this-tab';
}

/**
 * CQG's own `LogonResult.result_code` for "you already have a session elsewhere"
 * (user_session_2.proto RESULT_CODE_CONCURRENT_SESSION) — the take-over shape of a kick.
 */
const RESULT_CODE_CONCURRENT_SESSION = 105;

/**
 * A logon CQG ACCEPTED ends the reason for the latch (2026-09-14): a kick, an earlier rejection or a
 * Disconnect the user has since undone are all settled by a working session, so the next page load may
 * auto-resume again. Only an explicit Connect used to clear it, which left healthy sessions unresumable.
 */
function clearCqgLatchAfterAcceptedLogon(): void {
  if (!_autoResumeBlocked && readAutoResumeLatch('cqg') === null) return;
  _autoResumeBlocked = false;
  releaseAutoResume('cqg');
}

/** The shared app-level CQG relay (lazily created). */
export function getCqgRelay(): CqgRelay {
  _singleton ??= new CqgRelay(() => _tokenGetter());
  return _singleton;
}

/**
 * Abort an in-progress (not-yet-live) CQG connect/resume — the "Cancel" on the connecting dialog.
 * Tells the BE to drop the session (which pushes `CqgCloseSocket` back, closing the browser socket)
 * AND stops this tab's relay, so nothing keeps re-opening the socket in the background. Best-effort:
 * BOTH run even if one fails, so Cancel always cancels. (A LIVE session is torn down via the
 * Disconnect button, not this — see `cancelImporting`.)
 */
export async function abortCqgConnection(opts: {
  disconnect: () => Promise<unknown>;
  stopRelay: () => Promise<void>;
}): Promise<void> {
  await Promise.allSettled([
    Promise.resolve().then(opts.disconnect),
    Promise.resolve().then(opts.stopRelay),
  ]);
}
